import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';

class ProductRecommendations extends Component {
  #intersectionObserver = new IntersectionObserver((entries, observer) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    this.#loadRecommendations();
  }, { rootMargin: '0px 0px 400px 0px' });

  #mutationObserver = new MutationObserver((mutations) => {
    if (mutations.some((mutation) =>
      mutation.target === this &&
      ['data-product-id', 'data-section-id', 'data-intent', 'data-url'].includes(mutation.attributeName)
    )) {
      this.dataset.recommendationsPerformed = 'false';
      this.#loadRecommendations();
    }
  });

  #cachedRecommendations = new Map();
  #activeFetch = null;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#observeAttributes();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
    this.#generation++;
  }

  #observeAttributes() {
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-product-id', 'data-section-id', 'data-intent', 'data-url'],
    });
  }

  async #loadRecommendations() {
    const { productId, sectionId, intent, recommendationsPerformed, url: baseUrl } = this.dataset;
    if (recommendationsPerformed === 'true') return;
    if (!productId || !sectionId || !baseUrl) return;

    const generation = ++this.#generation;
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('intent', intent || 'related');
      const cacheKey = url.toString();
      let html = this.#cachedRecommendations.get(cacheKey);
      if (!html) {
        const response = await fetch(cacheKey, { signal: controller.signal });
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        html = await response.text();
        this.#cachedRecommendations.set(cacheKey, html);
      }
      if (generation !== this.#generation || !this.isConnected) return;

      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = documentFragment.getElementById(this.id);
      if (!replacement || replacement.tagName !== this.tagName) {
        throw new Error('Matching recommendation block missing from response');
      }
      if (replacement.dataset.intent !== intent) {
        throw new Error('Recommendation type does not match the requesting block');
      }

      // The endpoint renders the entire parent section using one recommendation intent.
      // Only copy this block, never its sibling recommendation blocks.
      replacement.dataset.recommendationsPerformed = 'true';
      this.#mutationObserver.disconnect();
      try {
        morph(this, replacement, { ...MORPH_OPTIONS, hydrationMode: false });
        this.dataset.recommendationsPerformed = 'true';
        this.classList.remove('hidden');
        delete this.dataset.error;
      } finally {
        if (this.isConnected) this.#observeAttributes();
      }
    } catch (error) {
      if (error.name === 'AbortError' || generation !== this.#generation) return;
      if (!Shopify.designMode) {
        console.error('Product recommendations error:', error.message);
        this.classList.add('hidden');
        this.dataset.error = 'Error loading product recommendations';
      }
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
